export type FitBucket = "STRONG" | "GOOD" | "FAIR"

// Mirrors the API's coarse fit buckets (apps/api matching-score.ts). We show
// these instead of a percentage: the score is a deterministic overlap measure,
// and a "87%" implied a precision it never had.
const BUCKET_META: Record<
  FitBucket,
  { label: string; className: string }
> = {
  STRONG: {
    label: "Strong match",
    className: "bg-[#3F7A6D]/90 text-white",
  },
  GOOD: {
    label: "Good match",
    className: "bg-[#75A096]/90 text-white",
  },
  FAIR: {
    label: "Fair match",
    className: "bg-[#C9A15A]/90 text-white",
  },
}

/**
 * Small overlay pill showing how well an event fits the member. Renders
 * nothing when there's no bucket (e.g. below-floor or legacy rows without a
 * score), so cards never show a misleading default.
 */
export function FitBadge({
  fitBucket,
  className = "",
}: {
  fitBucket?: FitBucket | null
  className?: string
}) {
  if (!fitBucket) return null
  const meta = BUCKET_META[fitBucket]
  return (
    <span
      className={`text-xs font-semibold px-2 py-1 rounded-full backdrop-blur-sm ${meta.className} ${className}`}
    >
      {meta.label}
    </span>
  )
}

/** Derive a bucket from a raw [0,1] score — for surfaces that only have the
 *  number (mirrors the API thresholds). */
export function bucketFromScore(score?: number | null): FitBucket | null {
  if (score == null) return null
  if (score >= 0.66) return "STRONG"
  if (score >= 0.4) return "GOOD"
  if (score >= 0.2) return "FAIR"
  return null
}
